import React, { useState, useEffect, useCallback } from 'react';
import Landing, { ArenaMode } from './components/Landing';
import Arena from './components/Arena';
import Reveal from './components/Reveal';
import ResultsSummary from './components/ResultsSummary';
import Leaderboard from './components/Leaderboard';
import LegalFooter from './components/LegalFooter';
import BenchmarkJudge from './components/BenchmarkJudge';
import { ArenaLang, LangContext, useT } from './i18n';

const TOTAL_ROUNDS = 5;

interface PairData {
  pairId: number;
  textA: string;
  textB: string;
  genre: string | null;
  humanSide: 'A' | 'B';
  round: number;
}

interface RevealData {
  correct: boolean;
  humanSource: string;
  humanYear: number | null;
  aiModel: string;
  humanSide: 'A' | 'B';
}

interface RoundResult {
  correct: boolean;
  humanSource: string;
  aiModel: string;
}

type Screen = 'landing' | 'arena' | 'reveal' | 'results' | 'leaderboard';

function LoadingText() {
  const t = useT();
  return <p className="text-stone-400 text-sm font-serif italic">{t('arena.loading')}</p>;
}

function getSessionId(): string {
  let id = localStorage.getItem('arena-session-id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('arena-session-id', id);
  }
  return id;
}

function detectBrowserLang(): ArenaLang {
  const stored = localStorage.getItem('arena-lang');
  if (stored === 'en' || stored === 'de') return stored;
  const browserLang = navigator.language.slice(0, 2);
  return browserLang === 'de' ? 'de' : 'en';
}

const IS_BENCHMARK = window.location.pathname.startsWith('/benchmark');

export default function App() {
  if (IS_BENCHMARK) return <BenchmarkJudge />;
  return <QuizApp />;
}

function QuizApp() {
  const [screen, setScreen] = useState<Screen>('landing');
  const [sessionId] = useState(getSessionId);
  const [currentPair, setCurrentPair] = useState<PairData | null>(null);
  const [revealData, setRevealData] = useState<RevealData | null>(null);
  const [results, setResults] = useState<RoundResult[]>([]);
  const [round, setRound] = useState(0);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<ArenaMode>('literatur');
  const [lang, setLang] = useState<ArenaLang>(detectBrowserLang);

  const changeLang = (newLang: ArenaLang) => {
    setLang(newLang);
    localStorage.setItem('arena-lang', newLang);
  };

  const fetchNextPair = useCallback(
    async (overrideMode?: ArenaMode) => {
      const activeMode = overrideMode || mode;
      setLoading(true);
      try {
        const res = await fetch(
          `/api/pairs/next?sessionId=${sessionId}&mode=${activeMode}&lang=${lang}`
        );
        const data = await res.json();
        if (data.done) {
          setScreen('results');
        } else {
          setCurrentPair(data);
          setScreen('arena');
        }
      } catch (err) {
        console.error('Error fetching pair:', err);
      } finally {
        setLoading(false);
      }
    },
    [sessionId, mode, lang]
  );

  const handleStart = () => {
    // Reset for new game
    setResults([]);
    setRound(0);
    // Clear old session for a fresh game
    const newId = crypto.randomUUID();
    localStorage.setItem('arena-session-id', newId);
    window.location.reload();
  };

  const handleStartFirst = (selectedMode: ArenaMode) => {
    setMode(selectedMode);
    fetchNextPair(selectedMode);
  };

  const handleVote = async (chosenSide: 'A' | 'B', timeSpentMs: number) => {
    if (!currentPair) return;

    setLoading(true);
    try {
      const res = await fetch('/api/votes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pairId: currentPair.pairId,
          sessionId,
          chosenSide,
          humanSide: currentPair.humanSide,
          timeSpentMs,
        }),
      });
      const data: RevealData = await res.json();
      setRevealData(data);
      setResults((prev) => [
        ...prev,
        {
          correct: data.correct,
          humanSource: data.humanSource,
          aiModel: data.aiModel,
        },
      ]);
      setRound((prev) => prev + 1);
      setScreen('reveal');
    } catch (err) {
      console.error('Error submitting vote:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNextRound = () => {
    if (round >= TOTAL_ROUNDS) {
      setScreen('results');
    } else {
      fetchNextPair();
    }
  };

  // Loading screen between rounds
  if (loading && screen !== 'arena') {
    return (
      <LangContext.Provider value={lang}>
        <div className="min-h-screen flex flex-col">
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-600 rounded-full animate-spin mx-auto mb-4" />
              <LoadingText />
            </div>
          </div>
          <LegalFooter />
        </div>
      </LangContext.Provider>
    );
  }

  return (
    <LangContext.Provider value={lang}>
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 animate-fadeIn">
          {screen === 'landing' && (
            <Landing
              onStart={handleStartFirst}
              onShowLeaderboard={() => setScreen('leaderboard')}
              lang={lang}
              onChangeLang={changeLang}
            />
          )}

          {screen === 'arena' && currentPair && (
            <Arena
              pair={currentPair}
              round={round + 1}
              totalRounds={TOTAL_ROUNDS}
              onVote={handleVote}
              loading={loading}
            />
          )}

          {screen === 'reveal' && revealData && currentPair && (
            <Reveal
              reveal={revealData}
              pair={currentPair}
              round={round}
              totalRounds={TOTAL_ROUNDS}
              onNext={handleNextRound}
            />
          )}

          {screen === 'results' && (
            <ResultsSummary
              results={results}
              sessionId={sessionId}
              onPlayAgain={handleStart}
              onShowLeaderboard={() => setScreen('leaderboard')}
            />
          )}

          {screen === 'leaderboard' && (
            <Leaderboard
              sessionId={sessionId}
              onBack={() => setScreen(results.length > 0 ? 'results' : 'landing')}
            />
          )}
        </div>
        <LegalFooter />
      </div>
    </LangContext.Provider>
  );
}
